import type { ReactNode } from 'react';
import type { MenuLink } from '@/menu-links';
import { Beaker, BookOpen, Compass, CreditCard, Flag, FlaskConical, LogIn, Mail, Megaphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from 'hooks/useAuth';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { NavLink } from '@/components/NavLink';
import { cn } from 'helpers/cn';
import { getMenuLinks } from '@/menu-links';
import { useOrganizations } from '@/state/useOrganizations';
import { useWebsites } from '@/state/useWebsites';
import { useIsEdgeEnvironment } from '@/state/useIsEdgeEnvironment';
import { useIsMobile } from '@/components/ui/hooks/use-mobile';
import { ScopePickers } from './ScopePickers';
import { useTourReplay } from '@/tours';

/**
 * A section of the sidebar: a mono heading and the rows under it.
 *
 * `menu-links` is a route tree, not a display tree — its top level mixes leaf
 * routes (Dashboard, Clients) with groups (Experiments, Settings). The design
 * shows a flat list of headed sections, so the leading leaves are collected under
 * a synthetic OVERVIEW heading and every group becomes a section of its own.
 * Settings and the Admin block collapse instead: see CollapsibleSection.
 */
type NavSection = { key: string; heading: string; items: MenuLink[] };

const isVisible = (item: MenuLink) => !item.ignore && !item.hideInMenu;

/**
 * Sections folded into an accordion, by key, with the route prefix that opens them.
 * Visited now and then rather than every day, so they should not cost a row each on
 * every page.
 */
const COLLAPSED_SECTIONS: Record<string, string> = { settings: '/settings' };

const buildSections = (links: MenuLink[]): NavSection[] => {
  const sections: NavSection[] = [];
  const overview: MenuLink[] = [];

  for (const item of links) {
    if (!isVisible(item)) continue;
    if (!item.children) {
      overview.push(item);
      continue;
    }
    const items = item.children.filter(isVisible);
    if (items.length) sections.push({ key: item.key, heading: item.label, items });
  }

  if (overview.length) sections.unshift({ key: 'overview', heading: 'Overview', items: overview });
  return sections;
};

type ConsoleSidebarProps = {
  /** Set by the layout to float the sidebar over the page as a drawer on mobile. */
  className?: string;
  /** Desktop only: icons alone, each named by a tooltip. The phone drawer is always full. */
  isRail?: boolean;
};

export const ConsoleSidebar = ({ className, isRail = false }: ConsoleSidebarProps) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { options: organizations } = useOrganizations();
  const { options: websites } = useWebsites();

  if (!user) return null;

  const sections = buildSections(getMenuLinks({ user }));

  // Only counts already loaded by the shell itself. A count that would need its
  // own request stays absent rather than making every page pay for a number.
  const counts: Record<string, number | undefined> = {
    clients: organizations?.length,
    'websites.list': websites?.length,
  };

  // The width animates; the contents do not. They are laid out at the width of the
  // state being entered and the aside's overflow clips them while it catches up, so
  // no label rewraps or re-truncates on every frame of the move.
  return (
    <aside
      className={cn(
        'bg-panel border-line ease-out flex shrink-0 flex-col overflow-hidden rounded-panel border transition-[width] duration-200',
        isRail ? 'w-14' : 'w-[228px]',
        className,
      )}
    >
      <TooltipProvider delayDuration={200}>
        <div className={cn('flex min-h-0 flex-1 flex-col', isRail ? 'w-[54px]' : 'w-[226px]')}>
          {/* On a phone the header has no room for the scope pickers, so the drawer
              hosts them - and this is where they lived before the redesign anyway. */}
          {isMobile && (
            <div className="border-line shrink-0 border-b p-3">
              <ScopePickers stacked />
            </div>
          )}

          {/* 9px a side in the rail centres a 36px button in the 54px inside the hairline. */}
          <nav className={cn('min-h-0 flex-1 overflow-y-auto py-3', isRail ? 'px-[9px]' : 'px-2')}>
            {sections.map((section, index) => {
              const rows = section.items.map((item) => (
                <NavRow key={item.key} item={item} count={counts[item.key]} isRail={isRail} />
              ));
              if (isRail)
                return (
                  <RailGroup
                    key={section.key}
                    sectionKey={section.key}
                    heading={section.heading}
                    hasDivider={index > 0}
                  >
                    {rows}
                  </RailGroup>
                );
              const openOn = COLLAPSED_SECTIONS[section.key];
              if (openOn)
                return (
                  <CollapsibleSection
                    key={section.key}
                    sectionKey={section.key}
                    heading={section.heading}
                    openOn={openOn}
                  >
                    {rows}
                  </CollapsibleSection>
                );
              return (
                // `nav-<key>` anchors the platform tour's steps on this section.
                <div key={section.key} className="mb-1.5" data-tour={`nav-${section.key}`}>
                  <div className="micro-label px-2 pb-1.5 pt-2.5">{section.heading}</div>
                  {rows}
                </div>
              );
            })}
            <AdminSection isRail={isRail} />
          </nav>

          {isRail ? (
            <div className="border-line flex shrink-0 flex-col gap-0.5 border-t px-[9px] py-2">
              <ToursMenu isRail />
              <DocumentationLink isRail />
            </div>
          ) : (
            <>
              <ToursMenu isRail={false} />
              <DocumentationLink isRail={false} />
            </>
          )}
        </div>
      </TooltipProvider>
    </aside>
  );
};

/** A rail button: the full row's border and radius on a 36px square. */
const RAIL_BUTTON_CLASS =
  'flex size-9 shrink-0 items-center justify-center rounded-lg border border-transparent transition-colors duration-150';

const RAIL_FOOTER_BUTTON_CLASS = cn(RAIL_BUTTON_CLASS, 'text-dim hover:bg-hover hover:text-foreground');

/** A full row's label. It fades in as the rail opens, rather than arriving ahead of the width. */
const LABEL_CLASS = 'ease-out min-w-0 flex-1 truncate transition-opacity duration-200 starting:opacity-0';

/** A full footer row: a hairline above, pinned under the scroll area. */
const FOOTER_ROW_CLASS =
  'border-line text-dim hover:bg-hover hover:text-foreground flex shrink-0 items-center gap-2.5 border-t px-4 py-[9px] text-left text-body transition-colors duration-150';

type RailTooltipProps = { label: string; children: ReactNode };

/** The rail has no words, so each icon names itself on hover and on focus. */
const RailTooltip = ({ label, children }: RailTooltipProps) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="right" className="text-body">
      {label}
    </TooltipContent>
  </Tooltip>
);

type RailGroupProps = { sectionKey: string; heading: string; hasDivider: boolean; children: ReactNode };

/**
 * A section in the rail. Its heading becomes a short hairline between groups, and stays the
 * group's accessible name so a screen reader still hears where a section starts. The
 * `nav-<key>` tour anchor stays too, so the platform tour finds the section in either state.
 */
const RailGroup = ({ sectionKey, heading, hasDivider, children }: RailGroupProps) => (
  <div role="group" aria-label={heading} data-tour={`nav-${sectionKey}`}>
    {hasDivider && <div aria-hidden="true" className="bg-line mx-auto my-2 h-px w-5" />}
    {children}
  </div>
);

type NavRowProps = { item: MenuLink; count?: number; isRail: boolean };

const NavRow = ({ item, count, isRail }: NavRowProps) => {
  const location = useLocation();
  const { label, url, icon: Icon } = item;
  const isActive = url === location.pathname || (url !== '/' && location.pathname.startsWith(`${url}/`));
  const tone = isActive ? 'border-sel-line bg-sel-bg text-foreground' : 'text-dim hover:bg-hover hover:text-foreground';
  // The tone is colour alone, so say it to a screen reader too. Our NavLink is a plain Link
  // underneath, which sets no aria-current of its own.
  const ariaCurrent = isActive ? 'page' : undefined;

  // No count or beta chip in the rail: there is no room beside an icon, and a beta
  // page still says so in the toolbar once it is open.
  if (isRail)
    return (
      <RailTooltip label={label}>
        <NavLink
          to={url!}
          data-tour={`nav-${item.key}`}
          aria-label={label}
          aria-current={ariaCurrent}
          className={cn(RAIL_BUTTON_CLASS, tone)}
        >
          <Icon className="size-[15px] shrink-0" strokeWidth={1.5} />
        </NavLink>
      </RailTooltip>
    );

  return (
    <NavLink
      to={url!}
      data-tour={`nav-${item.key}`}
      aria-current={ariaCurrent}
      className={cn(
        'flex items-center gap-2.5 rounded-lg border border-transparent px-2 py-[7px] text-body transition-colors duration-150',
        tone,
      )}
    >
      <Icon className="size-[15px] shrink-0" strokeWidth={1.5} />
      <span className={LABEL_CLASS}>{label}</span>
      {item.beta && <BetaChip />}
      {!item.beta && count !== undefined && <span className="micro-label tracking-normal">{count}</span>}
    </NavLink>
  );
};

type FooterRowProps = { isRail: boolean };

const DOCUMENTATION_URL = 'https://pertento.ai/resources';

/**
 * External, so a plain anchor rather than NavRow: it opens in a new tab and can
 * never be the active route. Pinned under the scroll area so it is findable
 * without scrolling a long nav.
 */
const DocumentationLink = ({ isRail }: FooterRowProps) => {
  const icon = <BookOpen className="size-[15px] shrink-0" strokeWidth={1.5} />;

  if (isRail)
    return (
      <RailTooltip label="Documentation">
        <a
          href={DOCUMENTATION_URL}
          target="_blank"
          rel="noreferrer"
          aria-label="Documentation"
          className={RAIL_FOOTER_BUTTON_CLASS}
        >
          {icon}
        </a>
      </RailTooltip>
    );

  return (
    <a href={DOCUMENTATION_URL} target="_blank" rel="noreferrer" className={FOOTER_ROW_CLASS}>
      {icon}
      <span className={LABEL_CLASS}>Documentation</span>
    </a>
  );
};

/**
 * Replays a tour on demand. Pinned beside Documentation, where someone looking for help
 * already looks, and the last stop of the platform tour points here.
 *
 * In the rail the menu opens to the right of the button, out over the workspace,
 * rather than upwards out of a 56px column.
 */
const ToursMenu = ({ isRail }: FooterRowProps) => {
  const { hasPageTour, replayPlatform, replayPage } = useTourReplay();
  const icon = <Compass className="size-[15px] shrink-0" strokeWidth={1.5} />;

  return (
    <DropdownMenu>
      {isRail ? (
        <RailTooltip label="Tours">
          <DropdownMenuTrigger data-tour="tour-help" aria-label="Tours" className={RAIL_FOOTER_BUTTON_CLASS}>
            {icon}
          </DropdownMenuTrigger>
        </RailTooltip>
      ) : (
        <DropdownMenuTrigger data-tour="tour-help" className={FOOTER_ROW_CLASS}>
          {icon}
          <span className={LABEL_CLASS}>Tours</span>
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent side={isRail ? 'right' : 'top'} align={isRail ? 'end' : 'start'} className="w-56">
        <DropdownMenuItem onSelect={replayPlatform}>Take the platform tour</DropdownMenuItem>
        <DropdownMenuItem onSelect={replayPage} disabled={!hasPageTour}>
          Show me this page
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

type CollapsibleSectionProps = {
  sectionKey: string;
  heading: string;
  /** The route prefix inside this section. Arriving on one opens it, so the active row is never hidden. */
  openOn: string;
  children: ReactNode;
};

/**
 * A sidebar section folded into an accordion. Closed by default, open while the reader
 * is inside it, and collapsible by hand either way. The heading stays the platform
 * tour's `nav-<key>` anchor, so a step on the section still finds it when closed.
 * The rail has no accordions: there the section is a RailGroup like any other.
 */
const CollapsibleSection = ({ sectionKey, heading, openOn, children }: CollapsibleSectionProps) => {
  const { pathname } = useLocation();
  const isInside = pathname === openOn || pathname.startsWith(`${openOn}/`);
  const [isOpen, setIsOpen] = useState(isInside);

  // Following a link into the section opens it; leaving does not close it again.
  useEffect(() => {
    if (isInside) setIsOpen(true);
  }, [isInside]);

  return (
    <Accordion
      type="single"
      collapsible
      value={isOpen ? sectionKey : ''}
      onValueChange={(value) => setIsOpen(value === sectionKey)}
    >
      <AccordionItem value={sectionKey} className="border-b-0" data-tour={`nav-${sectionKey}`}>
        <AccordionTrigger className="micro-label hover:text-foreground px-2 pb-1.5 pt-2.5 hover:no-underline [&>svg]:size-3.5">
          {heading}
        </AccordionTrigger>
        <AccordionContent className="pb-1.5">{children}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

type AdminSectionProps = { isRail: boolean };

/**
 * Super Admin destinations. These are hardcoded rather than read from
 * `menu-links` because that tree marks the whole Admin Tools branch
 * `hideInMenu` — it exists there to register routes, not to be listed.
 * Occasional tools, so the section is collapsed like Settings. In the rail it
 * is a group of icons under a divider instead, as Settings is: a heading with
 * no words cannot be an accordion's trigger.
 */
const AdminSection = ({ isRail }: AdminSectionProps) => {
  const { user, isAuthenticated } = useAuth();
  const isEdge = useIsEdgeEnvironment();

  if (!isAuthenticated || user.role !== 'Super Admin') return null;

  const links: MenuLink[] = [
    { key: 'admin.announcement', label: 'Announcement', url: '/admin/announcement', icon: Megaphone },
    { key: 'admin.signinPanel', label: 'Signin Panel', url: '/admin/signin-panel', icon: LogIn },
    { key: 'admin.emailer', label: 'Emailer', url: '/admin/emailer', icon: Mail },
    { key: 'admin.billing', label: 'Billing', url: '/admin/billing', icon: CreditCard },
    { key: 'admin.betaFeatures', label: 'Beta Features', url: '/admin/beta-features', icon: Beaker },
    ...(isEdge ? [{ key: 'admin.featureFlags', label: 'Branch Flags', url: '/admin/feature-flags', icon: Flag }] : []),
    { key: 'admin.testExperiments', label: 'Test Experiments', url: '/admin/test-experiments', icon: FlaskConical },
  ];

  const rows = links.map((item) => <NavRow key={item.key} item={item} isRail={isRail} />);

  if (isRail)
    return (
      <RailGroup sectionKey="admin" heading="Admin" hasDivider>
        {rows}
      </RailGroup>
    );

  return (
    <CollapsibleSection sectionKey="admin" heading="Admin" openOn="/admin">
      {rows}
    </CollapsibleSection>
  );
};

export const BetaChip = () => (
  <span className="border-sel-line bg-sel-bg text-sel rounded-pill border px-1.5 py-px font-mono text-nano font-medium uppercase tracking-[0.1em]">
    beta
  </span>
);

/*
 * The design puts a "tracker 0.9 KB / runtime version" strip at the foot of the
 * sidebar. Neither number is available to the dashboard: the weight was a
 * constant copied from the prototype, and the per-website runtime version is not
 * exposed by `useWebsites`. A hard-coded figure that never changes is worse than
 * no figure, so the strip is gone until something can populate it.
 */
